import type { DashboardPayload, ThisWeekItem } from "../types/dashboard";
import { payloadTimeZone } from "./timezone";

// Personalized greeting line: `${worker.greeting}, ${displayName}.`
// Worker returns the short "Good morning" / "Good afternoon" /
// "Good evening" form; rendered as-is. Empty displayName → no name,
// no trailing comma, no fallback to email-local-part.
export function formatGreeting(
  workerGreeting: string,
  displayName: string | undefined | null,
): string {
  const name = displayName?.trim();
  return name ? `${workerGreeting}, ${name}.` : `${workerGreeting}.`;
}

// Joins clauses with serial commas and a final "and".
function joinClauses(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

// Mass-specific summary. Names each Mass that's short and which
// ministries are short there, so Colleen sees "Sunday 9am and 5:30pm
// are each short on EMHC" instead of "N Masses need attention."
// Same-ministry Masses are collapsed so the parish-accent reads as
// one cluster. Multi-ministry Masses ("Sun 9am is short on EMHC and
// Lector") render individually since their gap profile is unique.

interface MassGap {
  startsAt: string;
  weekday: string;
  time: string;
  ministries: Set<string>;
}

// startsAt is a real UTC instant (refactor PR A); format it in the
// parish timezone the payload carries.
function parseMassTime(
  startsAt: string,
  timeZone: string,
): { weekday: string; time: string } {
  const d = new Date(startsAt);
  const weekday = d.toLocaleString("en-US", { weekday: "long", timeZone });
  const raw = d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
  // "5:00 PM" → "5pm"; "11:30 AM" → "11:30am".
  const time = raw
    .replace(/:00\s+(AM|PM)/i, (_, p: string) => p.toLowerCase())
    .replace(/\s+(AM|PM)/i, (_, p: string) => p.toLowerCase());
  return { weekday, time };
}

function formatMassList(
  masses: Array<{ weekday: string; time: string }>,
): string {
  // Collapse consecutive same-weekday entries — "Sunday 9am and
  // 5:30pm" reads tighter than "Sunday 9am and Sunday 5:30pm".
  const tokens: string[] = [];
  let lastWeekday = "";
  for (const m of masses) {
    if (m.weekday === lastWeekday) {
      tokens.push(m.time);
    } else {
      tokens.push(`${m.weekday} ${m.time}`);
      lastWeekday = m.weekday;
    }
  }
  return joinClauses(tokens);
}

function summarizeMassGaps(items: ThisWeekItem[], timeZone: string): string[] {
  const gapsByMass: MassGap[] = [];
  for (const item of items) {
    if (item.status !== "needs_attention") continue;
    if (item.gaps.length === 0) continue;
    const { weekday, time } = parseMassTime(item.startsAt, timeZone);
    gapsByMass.push({
      startsAt: item.startsAt,
      weekday,
      time,
      ministries: new Set(item.gaps.map((g) => g.ministry)),
    });
  }
  gapsByMass.sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  // Single-ministry Masses can collapse by ministry: "Sunday 9am and
  // 5:30pm are each short on EMHC". Multi-ministry Masses render
  // individually because their gap shape is unique.
  const singleByName = new Map<string, MassGap[]>();
  const multi: MassGap[] = [];
  for (const m of gapsByMass) {
    if (m.ministries.size === 1) {
      const ministry = [...m.ministries][0]!;
      const list = singleByName.get(ministry) ?? [];
      list.push(m);
      singleByName.set(ministry, list);
    } else {
      multi.push(m);
    }
  }

  const clauses: string[] = [];
  for (const [ministry, masses] of singleByName) {
    const list = formatMassList(masses);
    const verb = masses.length === 1 ? "is" : "are each";
    clauses.push(`${list} ${verb} short on ${ministry}`);
  }
  for (const m of multi) {
    const ministries = [...m.ministries].sort();
    clauses.push(
      `${m.weekday} ${m.time} is short on ${joinClauses(ministries)}`,
    );
  }
  return clauses;
}

// Assembles the summary sentence. Mass coverage only — quarterly
// prep, the outreach-pending template, and the watch list are
// surfaced elsewhere in the dashboard; the greeting line keeps
// Colleen's attention on what's short for this week's Masses.
export function buildSummary(payload: DashboardPayload): string {
  const timeZone = payloadTimeZone(payload.timezone);
  const massGapClauses = summarizeMassGaps(payload.thisWeek, timeZone);
  const clauses: string[] =
    massGapClauses.length === 0
      ? ["all Masses this week are good to go"]
      : massGapClauses;

  const joined = joinClauses(clauses);
  return `${joined.charAt(0).toUpperCase()}${joined.slice(1)}.`;
}
